<template>
  <b-container fluid>
    <admin-preloader :show="loading"/>
    <b-row>
      <b-col>
        <admin-breadcrumb :items="breadcrumbItems" :name="breadcrumbName" noTitle/>
      </b-col>
    </b-row>
    <b-row v-if="primaryLicense" class="mb-3 align-items-start">
      <b-col>
        <h2 v-if="displayNames.length > 0" class="mb-0">{{ displayNames[0] }}</h2>
        <h5 v-if="displayNames.length > 1" class="text-muted mb-0">{{ displayNames[1] }}</h5>
        <small v-if="displayNames.length > 2" class="text-muted">{{ displayNames[2] }}</small>
        <div class="mt-1">
          <b-badge :variant="primaryLicense.status === 'Approved' ? 'success' : 'secondary'" class="mr-1">{{ primaryLicense.status }}</b-badge>
          <b-badge v-if="primaryLicense.license_type === 'facility'" variant="info" class="mr-1">Facility</b-badge>
          <b-badge v-if="primaryLicense.license_type === 'owner'" variant="info" class="mr-1">Owner</b-badge>
          <b-badge v-if="primaryLicense.license_type === 'owner_entity'" variant="info" class="mr-1">Owner Entity</b-badge>
        </div>
      </b-col>
      <b-col class="text-right">
        <h4 class="mb-1">{{ primaryLicense.license_number }}</h4>
        <div v-if="primaryLicense.street || primaryLicense.city" class="text-muted">
          {{ [primaryLicense.street, primaryLicense.city, primaryLicense.state].filter(Boolean).join(', ') }}
          {{ primaryLicense.zip_code }}
        </div>
      </b-col>
    </b-row>
    <b-modal id="create-dba-modal" title="Create New DBA" @ok="createDba">
      <template #modal-footer="{ ok, cancel }">
        <b-button variant="secondary" @click="cancel()">Cancel</b-button>
        <b-button variant="primary" @click="ok()">Create</b-button>
      </template>
      <b-form-group
          label="DBA Name"
          label-for="new-dba-name-input"
      >
        <b-form-input
            id="new-dba-name-input"
            v-model="newDbaName"
            required
        ></b-form-input>
      </b-form-group>
    </b-modal>
    <b-row>
      <b-col cols="12">
        <admin-panel>
          <b-tabs v-model="activeTab" content-class="pt-3" fill>
            <b-tab>
              <template #title>Associated Facilities <b-badge variant="light">{{ items.length }}</b-badge></template>
              <div class="d-flex justify-content-end align-items-center mb-3">
                <v-select
                    v-model="selectedDbaForBulkAssign"
                    :options="dbaOptions"
                    label="name"
                    :reduce="option => option.value"
                    placeholder="Select a DBA to assign"
                    class="mr-2"
                    style="width: 300px;"
                >
                  <template #option="option">
                    <strong v-if="option.type === 'existing'">{{ option.name }}</strong>
                    <em v-else-if="option.type === 'suggested'">{{ option.name }}</em>
                    <span v-else>{{ option.name }}</span>
                  </template>
                </v-select>
                <no-permission-wrapper :show="!canUpdateStateLicenses">
                  <b-button variant="primary" @click="bulkAssignDba" :disabled="!selectedDbaForBulkAssign || selectedRows.length === 0 || !canUpdateStateLicenses" class="mr-2">Assign DBA</b-button>
                  <b-button variant="info" v-b-modal.create-dba-modal :disabled="!canUpdateStateLicenses">Create DBA</b-button>
                </no-permission-wrapper>
              </div>
              <b-table
                  ref="licensesTable"
                  :fields="fields"
                  :items="items"
                  class="associated-licenses-table"
                  responsive
                  hover
                  selectable
                  select-mode="multi"
                  @row-selected="onRowSelected"
              >
                <template #head(selected)="{ selectAllRows, clearSelected }">
                  <b-form-checkbox
                      @change="evt => { if (evt) { selectAllRows() } else { clearSelected() } }"
                      :checked="selectedRows.length === items.length && items.length > 0"
                      :indeterminate="selectedRows.length > 0 && selectedRows.length < items.length"
                  ></b-form-checkbox>
                </template>
                <template #cell(selected)="{ rowSelected, selectRow, unselectRow }">
                  <b-form-checkbox
                      :checked="rowSelected"
                      @change="rowSelected ? unselectRow() : selectRow()"
                  ></b-form-checkbox>
                </template>
                <template #cell(license_number)="{ item }">
                  <router-link :to="{ name: 'admin-state-license-detail', params: { id: item.id } }">
                    {{ item.license_number }}
                  </router-link>
                  <span
                      v-if="item.is_on_greenlists"
                      class="greenlists-indicator ml-1"
                      v-b-tooltip.hover
                      title="On Greenlists"
                  >
                      G
                  </span>
                </template>
                <template #cell(location)="{ item }">
                  <a
                      v-if="item.street || item.city"
                      :href="`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([item.street, item.city, item.state, item.zip_code].filter(Boolean).join(', '))}`"
                      target="_blank"
                      class="location-link"
                  >
                    {{ [item.street, item.city, item.state, item.zip_code].filter(Boolean).join(', ') }}
                    <b-icon icon="geo-alt" class="ml-1" font-scale="0.9"></b-icon>
                  </a>
                  <span v-else>—</span>
                </template>
                <template #cell(enriched_at)="{ item }">
                  {{ formatDate(item.enriched_at) }}
                </template>
                <template #table-caption>
                  <div class="text-right">
                    <b-button variant="secondary" class="mr-2" @click="resetDbaAssignments">Reset DBA Assignments</b-button>
                    <b-button variant="primary" @click="saveDbaAssignments">Save DBA Assignments</b-button>
                  </div>
                </template>
              </b-table>
            </b-tab>
            <b-tab>
              <template #title>Owner Entity <b-badge variant="light">{{ ownerEntityLicenses.length }}</b-badge></template>
              <b-table
                  :fields="linkedLicensesFields"
                  :items="ownerEntityLicenses"
                  :tbody-tr-class="linkedRowClass"
                  class="associated-licenses-table"
                  responsive
                  hover
                  show-empty
              >
                <template #cell(licenseNumber)="{ item }">
                  <router-link
                      v-if="item.id"
                      :to="{ name: 'admin-state-license-detail', params: { id: item.id } }"
                  >
                    {{ item.licenseNumber }}
                  </router-link>
                  <span v-else>{{ item.licenseNumber }}</span>
                </template>
              </b-table>
            </b-tab>
            <b-tab>
              <template #title>Owner <b-badge variant="light">{{ ownerLicenses.length }}</b-badge></template>
              <b-table
                  :fields="linkedLicensesFields"
                  :items="ownerLicenses"
                  :tbody-tr-class="linkedRowClass"
                  class="associated-licenses-table"
                  responsive
                  hover
                  show-empty
              >
                <template #cell(licenseNumber)="{ item }">
                  <router-link
                      v-if="item.id"
                      :to="{ name: 'admin-state-license-detail', params: { id: item.id } }"
                  >
                    {{ item.licenseNumber }}
                  </router-link>
                  <span v-else>{{ item.licenseNumber }}</span>
                </template>
              </b-table>
            </b-tab>
            <b-tab>
              <template #title>Expired Facilities <b-badge variant="light">{{ facilityLicenses.length }}</b-badge></template>
              <b-table
                  :fields="linkedLicensesFields"
                  :items="facilityLicenses"
                  :tbody-tr-class="linkedRowClass"
                  class="associated-licenses-table"
                  responsive
                  hover
                  show-empty
              >
                <template #cell(licenseNumber)="{ item }">
                  <router-link
                      v-if="item.id"
                      :to="{ name: 'admin-state-license-detail', params: { id: item.id } }"
                  >
                    {{ item.licenseNumber }}
                  </router-link>
                  <span v-else>{{ item.licenseNumber }}</span>
                </template>
              </b-table>
            </b-tab>
            <b-tab>
              <template #title>Other <b-badge variant="light">{{ otherLicenses.length }}</b-badge></template>
              <b-table
                  :fields="linkedLicensesFields"
                  :items="otherLicenses"
                  :tbody-tr-class="linkedRowClass"
                  class="associated-licenses-table"
                  responsive
                  hover
                  show-empty
              >
                <template #cell(licenseNumber)="{ item }">
                  <router-link
                      v-if="item.id"
                      :to="{ name: 'admin-state-license-detail', params: { id: item.id } }"
                  >
                    {{ item.licenseNumber }}
                  </router-link>
                  <span v-else>{{ item.licenseNumber }}</span>
                </template>
              </b-table>
            </b-tab>
          </b-tabs>
        </admin-panel>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import _ from 'lodash';
import moment from 'moment';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import AdminPanel from "../components/AdminPanel";
import {API_ROUTES} from "../api";
import AdminPreloader from "../components/AdminPreloader";
import AdminBreadcrumb from "../components/AdminBreadcrumb";
import NoPermissionWrapper from "../components/NoPermissionWrapper.vue";

export default {
  name: "StateLicenseDetailed",
  components: {
    NoPermissionWrapper,
    vSelect,
    AdminBreadcrumb,
    AdminPreloader,
    AdminPanel
  },
  props: {
    id: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      activeTab: 0,
      licenses: [],
      linkedLicenses: [],
      linkedLicensesFields: [
        { key: 'licenseNumber', label: 'License Number', thClass: "border-top-0", tdClass: "align-middle" },
        { key: 'licensee', label: 'Licensee', thClass: "border-top-0", tdClass: "align-middle" },
        { key: 'licenseType', label: 'License Type', thClass: "border-top-0", tdClass: "align-middle" },
        { key: 'licenseStatus', label: 'Status', thClass: "border-top-0", tdClass: "align-middle" }
      ],
      loading: true,
      suggested_dbas: [],
      all_dbas: [],
      selectedRows: [],
      selectedDbaForBulkAssign: null,
      newDbaName: '',
      fields: [
        {
          key: 'selected',
          label: '',
          thClass: "border-top-0",
          tdClass: 'align-middle'
        },
        {
          key: "license_number",
          label: 'License',
          thClass: "border-top-0",
          tdClass: "align-middle"
        },
        {
          key: "facility_name",
          label: "Facility Name",
          thClass: "border-top-0",
          tdClass: "align-middle"
        },
        {
          key: "status",
          label: "Status",
          thClass: "border-top-0",
          tdClass: "align-middle"
        },
        {
          key: "dba_name",
          label: "MED DBA",
          thClass: "border-top-0",
          tdClass: "align-middle"
        },
        {
          key: "selected_dba_name",
          label: "Assigned DBA",
          thClass: "border-top-0",
          tdClass: "align-middle"
        },
        {
          key: "location",
          label: "Location",
          thClass: "border-top-0",
          tdClass: "align-middle"
        },
        {
          key: "enriched_at",
          label: "Last Enriched",
          thClass: "border-top-0",
          tdClass: "align-middle"
        },
      ]
    }
  },
  computed: {
    canUpdateStateLicenses() {
      return this.$store.getters['auth/hasAdminPermissions']('state_license_update');
    },
    breadcrumbItems() {
      return [
        { text: 'State Licenses', path: { name: 'admin-state-licenses' } }
      ];
    },
    displayNames() {
      if (!this.primaryLicense) return [];
      return [
        this.primaryLicense.dba && this.primaryLicense.dba.name,
        this.primaryLicense.dba_name,
        this.primaryLicense.facility_name,
      ].filter(Boolean);
    },
    breadcrumbName() {
      if (this.primaryLicense) {
        return `License: ${this.primaryLicense.license_number}`;
      }
      return 'State License Detail';
    },
    primaryLicense() {
      if (!this.licenses || this.licenses.length === 0) {
        return null;
      }
      return this.licenses.find(license => license.id === Number(this.id)) || null;
    },
    filteredLinkedLicenses() {
      const associatedNumbers = new Set(this.licenses.map(l => l.license_number));
      return this.linkedLicenses.filter(ll => !associatedNumbers.has(ll.licenseNumber));
    },
    ownerEntityLicenses() {
      return this.sortAndFilterLinked(
        this.filteredLinkedLicenses.filter(ll => /owner[- ]*entity/i.test(ll.licenseType))
      );
    },
    ownerLicenses() {
      return this.sortAndFilterLinked(
        this.filteredLinkedLicenses.filter(ll =>
          /owner/i.test(ll.licenseType) && !/owner[- ]*entity/i.test(ll.licenseType)
        )
      );
    },
    facilityLicenses() {
      return this.sortAndFilterLinked(
        this.filteredLinkedLicenses.filter(ll =>
          !(/owner/i.test(ll.licenseType)) && /\d{3}R?-/.test(ll.licenseNumber)
        )
      );
    },
    otherLicenses() {
      return this.sortAndFilterLinked(
        this.filteredLinkedLicenses.filter(ll =>
          !(/owner/i.test(ll.licenseType)) && !/\d{3}R?-/.test(ll.licenseNumber)
        )
      );
    },
    items() {
      return this.licenses
        .filter(license => license.license_type === 'facility')
        .sort((a, b) => {
          const primaryId = Number(this.id);
          if (a.id === primaryId) return -1;
          if (b.id === primaryId) return 1;
          return 0;
        });
    },
    dbaOptions() {
      const options = [];
      const handledDbaIds = new Set();
      const handledDbaNames = new Set();

      // Group 1: Existing DBAs
      this.licenses.forEach(license => {
        if (license.dba && !handledDbaIds.has(license.dba.id)) {
          options.push({
            name: license.dba.name,
            value: license.dba.name,
            type: 'existing'
          });
          handledDbaIds.add(license.dba.id);
          handledDbaNames.add(license.dba.name.toLowerCase());
        }
      });

      // Group 2: Suggested DBAs
      this.suggested_dbas.forEach(dba => {
        if (!handledDbaNames.has(dba.name.toLowerCase())) {
          options.push({
            name: dba.name,
            value: dba.name,
            type: 'suggested'
          });
          handledDbaNames.add(dba.name.toLowerCase());
        }
      });

      // Group 3: Other DBAs
      this.all_dbas.forEach(dba => {
        if (!handledDbaIds.has(dba.id) && !handledDbaNames.has(dba.name.toLowerCase())) {
          options.push({
            name: dba.name,
            value: dba.name,
            type: 'other'
          });
          handledDbaNames.add(dba.name.toLowerCase());
        }
      });

      return options;
    }
  },
  created() {
    this.loadAssociatedLicenses();
    this.loadDbas();
  },
  watch: {
    id() {
      this.activeTab = 0;
      this.loadAssociatedLicenses();
    },
  },
  methods: {
    sortAndFilterLinked(licenses) {
      return [...licenses].sort((a, b) => {
        if (a.licenseStatus === 'Approved' && b.licenseStatus !== 'Approved') return -1;
        if (a.licenseStatus !== 'Approved' && b.licenseStatus === 'Approved') return 1;
        return a.licensee.localeCompare(b.licensee);
      });
    },
    linkedRowClass(item) {
      return item && item.licenseStatus !== 'Approved' ? 'linked-inactive' : '';
    },
    formatDate(dateString) {
      if (!dateString) return "";
      return moment(dateString).format("MM/DD/YYYY");
    },
    loadDbas() {
      this.$api.get(`${API_ROUTES.ADMIN_STATE_LICENSES}/dbas`)
          .then(({data}) => {
            this.all_dbas = data.result.data;
          }).catch((error) => {
        const message = error.response?.data?.message || 'Error loading DBAs';
        this.$bvToast.toast(message, {
          variant: 'danger',
          title: 'Error!'
        });
      });
    },
    loadAssociatedLicenses() {
      this.loading = true;
      this.$api.get(`${API_ROUTES.ADMIN_STATE_LICENSES}/associated/${this.id}`)
          .then(({data}) => {
            const shared_ownerships = data.result.data.shared_ownerships;
            this.licenses = shared_ownerships.map(license => ({
              ...license,
              selected_dba_name: license.dba ? license.dba.name : null,
            }));
            this.suggested_dbas = data.result.data.suggested_dbas;
            const allLinkedLicenses = shared_ownerships.flatMap(license => license.linked_licenses || []);
            const linkedLicenseIds = data.result.data.linked_license_ids || {};
            this.linkedLicenses = _.uniqBy(allLinkedLicenses, 'licenseNumber').map(ll => ({
              ...ll,
              id: linkedLicenseIds[ll.licenseNumber] || null,
            }));
          }).catch((error) => {
        const message = error.response?.data?.message || 'Error loading associated licenses';
        this.$bvToast.toast(message, {
          variant: 'danger',
          title: 'Error!'
        });
      }).finally(() => {
        this.loading = false;
      });
    },
    onRowSelected(items) {
      this.selectedRows = items;
    },
    bulkAssignDba() {
      if (!this.selectedDbaForBulkAssign || this.selectedRows.length === 0) {
        return;
      }
      const dbaNameToAssign = this.selectedDbaForBulkAssign;
      this.selectedRows.forEach(license => {
        license.selected_dba_name = dbaNameToAssign;
      });
      this.$refs.licensesTable.clearSelected();
    },
    resetDbaAssignments() {
      this.loadAssociatedLicenses();
    },
    createDba(bvModalEvt) {
      bvModalEvt.preventDefault();

      if (!this.canUpdateStateLicenses) {
        return this.$bvToast.toast('You do not have permission to create DBAs.', {
          variant: 'danger',
          title: 'Error!'
        })
      }

      const trimmedNewDbaName = this.newDbaName ? this.newDbaName.trim() : '';
      if (!trimmedNewDbaName) {
        this.$bvToast.toast('DBA name is required.', {
          variant: 'danger',
          title: 'Error!'
        });
        return;
      }

      const isDuplicate = this.all_dbas.some(
          dba => dba.name.toLowerCase() === trimmedNewDbaName.toLowerCase()
      );

      if (isDuplicate) {
        this.$bvToast.toast('A DBA with this name already exists.', {
          variant: 'danger',
          title: 'Error!'
        });
        return;
      }

      this.$api.post(`${API_ROUTES.ADMIN_STATE_LICENSES}/dbas`, {name: trimmedNewDbaName})
          .then(({data}) => {
            console.log('DBA creation response:', data);
            const newDba = data.result.data;
            this.all_dbas.push(newDba);
            this.selectedDbaForBulkAssign = newDba.name;
            this.newDbaName = '';
            this.$bvToast.toast('DBA created successfully!', {
              variant: 'success',
              title: 'Success!'
            });
            this.$root.$emit('bv::hide::modal', 'create-dba-modal');
          }).catch(error => {
        const message = error.response?.data?.message || 'Error creating DBA';
        this.$bvToast.toast(message, {
          variant: 'danger',
          title: 'Error!'
        });
      });
    },
    saveDbaAssignments() {
      if (!this.canUpdateStateLicenses) {
        return this.$bvToast.toast('You do not have permission to create DBAs.', {
          variant: 'danger',
          title: 'Error!'
        })
      }

      const assignments = this.licenses.reduce((acc, license) => {
        if (license.selected_dba_name) {
          acc[license.id] = license.selected_dba_name;
        }
        return acc;
      }, {});

      if (Object.keys(assignments).length === 0) {
        this.$bvToast.toast('No DBA assignments to save.', {
          variant: 'info',
          title: 'Info'
        });
        return;
      }

      const payload = { assignments };
      console.log('Payload:', payload);

      this.$api.post(`${API_ROUTES.ADMIN_STATE_LICENSES}/dbas/assign`, payload)
          .then(() => {
            this.$bvToast.toast('DBA assignments saved successfully!', {
              variant: 'success',
              title: 'Success!'
            });
            this.loadAssociatedLicenses();
            this.loadDbas();
          })
          .catch(error => {
            const message = error.response?.data?.message || 'Error saving DBA assignments';
            this.$bvToast.toast(message, {
              variant: 'danger',
              title: 'Error!'
            });
          });
    }
  }
}
</script>

<style scoped>
.page-title .badge {
  vertical-align: middle;
}
::v-deep .table td,
::v-deep .table th {
  padding: 0.4rem 0.5rem;
}

.associated-licenses-table {
  font-size: 12px;
}
::v-deep .linked-inactive td {
  opacity: 0.55;
}
::v-deep .nav-tabs {
  border-bottom-color: #a8cfef;
}
::v-deep .nav-tabs .nav-link {
  color: #343a40;
}
::v-deep .nav-tabs .nav-link.active {
  color: #007bff;
  border-color: #a8cfef #a8cfef #fff;
  border-top-left-radius: 0.375rem;
  border-top-right-radius: 0.375rem;
}
.location-link {
  text-decoration: none;
  color: inherit;
}
.location-link:hover {
  text-decoration: underline;
}
.greenlists-indicator {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  background-color: #28a745;
  color: white;
  border-radius: 50%;
  font-size: 10px;
  font-weight: bold;
  line-height: 1;
}
</style>
